import React, { useState, useEffect } from "react";
import { TextField, PrimaryButton, DefaultButton, Dropdown, IDropdownOption, Stack, Callout, TooltipHost, DirectionalHint, Checkbox, IconButton } from "@fluentui/react";
import styles from "./CCAFieldSelection.module.css";
import { FaFileImport } from "react-icons/fa";
import { TbArrowAutofitUp, TbArrowAutofitDown } from "react-icons/tb";
import { toast } from "react-toastify";
import { gfileassourceoption, gextrprop, extrtypesarray, gemptyextrprop, extrtypes } from "../../interfaces";
import {
    textFieldStylesDocExtr,
    textFieldStylesMultilineDocExtr,
    dropdownStylesDocExtr,
    textFieldStylesDocExtractImportScheme,
    calloutStylesExportDocCallout,
    dropdownFileSrcStylesDocExtr,
    ccaCheckBoxStyles,
    textFieldStylesMultilineShortDocExtr,
    answerLengthOptionsStyles,
    addScenPrimaryButton
} from "../../styles_glob";
import { FaTrashCan } from "react-icons/fa6";
import { answerLengthOptions, assistantCodes, extrTypeOptions } from "../../lsts";
import { type } from "microsoft-cognitiveservices-speech-sdk/distrib/lib/src/common.speech/SpeechServiceConfig";

interface PropertiesEditorProps {
    initialProperties?: gextrprop[] | [];
    onChange: (updatedProperties: gextrprop[]) => void;
    assistantType: string;
    isRoot?: boolean;
    fileList?: gfileassourceoption[];
    groupId?: string;
}

export const CCAFieldSelection: React.FC<PropertiesEditorProps> = ({ initialProperties, onChange, assistantType, isRoot, fileList, groupId }) => {
    const [prprts, setProperties] = useState<gextrprop[]>(initialProperties || []);
    const [isCalloutVisible, setIsCalloutVisible] = useState(false);
    const [importJson, setImportJson] = useState("");
    const [calloutTarget, setCalloutTarget] = useState<HTMLElement | null>(null);
    const [selectedIndex, setSelectedIndex] = useState("1");

    useEffect(() => {
        setProperties(initialProperties || []);
    }, [initialProperties]);

    const handleUpdate = (index: number, updatedFields: Partial<gextrprop>) => {
        const newProps = [...prprts];
        newProps[index] = { ...newProps[index], ...updatedFields };
        setProperties(newProps);
        onChange(newProps);
    };
    const handleSrcUpdate = (index: number, updatedFields: Partial<gextrprop>) => {
        const newProps = [...prprts];
        newProps[index] = { ...newProps[index], ...updatedFields };
        setProperties(newProps);

        onChange(newProps);
    };
    const handleRemove = (index: number) => {
        const newProps = [...prprts];
        newProps.splice(index, 1);
        setProperties(newProps);
        onChange(newProps);
    };

    const handleAdd = () => {
        import.meta.env.DEV && console.log("add", prprts);
        const newProps = [...(prprts || []), gemptyextrprop];
        setProperties(newProps);
        onChange(newProps);
    };

    const moveField = (fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= prprts.length || fromIndex === toIndex) return;
        const newProps = [...prprts];
        const movingItem = newProps[fromIndex];
        newProps.splice(fromIndex, 1);
        newProps.splice(toIndex, 0, movingItem);
        setProperties(newProps);
        onChange(newProps);
    };

    const handleMoveUp = (index: number) => {
        moveField(index, index - 1);
    };

    const handleMoveDown = (index: number) => {
        moveField(index, index + 1);
    };

    const handleImport = () => {
        try {
            const parsedJson = JSON.parse(importJson);
            import.meta.env.DEV && console.log("‹parsedJson›", parsedJson);

            let newProps = parseJsonToProperties(parsedJson);

            import.meta.env.DEV && console.log("‹newProps›", newProps);

            setProperties(newProps);
            onChange(newProps);
            setIsCalloutVisible(false);
            setImportJson('');
            toast.success("Schema imported successfully!", { autoClose: 1500 });
        } catch (e) {
            console.error("Invalid JSON", e, importJson);
            toast.error("Invalid JSON format. Please check your input.");
        }
    };

    const handleExport = () => {
        const schemaJson = JSON.stringify(prprts, null, 2);
        navigator.clipboard
            .writeText(schemaJson)
            .then(() => {
                toast.success("Schema copied to clipboard!", { autoClose: 1500 });
            })
            .catch(err => {
                console.error("Failed to copy schema:", err);
                toast.error("Failed to copy schema to clipboard");
            });
    };

    const importScheme = (event: React.MouseEvent<HTMLElement>) => {
        setCalloutTarget(event.currentTarget);
        setIsCalloutVisible(true);
    };

    function parseJsonToProperties(parsedJson: any): gextrprop[] {
        if (!Array.isArray(parsedJson)) throw new Error("Invalid JSON format. Expected an array of properties.");

        return parsedJson.map((item: any) => {
            if (typeof item !== "object" || item === null) throw new Error("Invalid property format. Expected an object.");

            const { name, description, type, validation_source, identificator, display_name, linked_fields, items } = item;

            if (typeof name !== "string" || typeof description !== "string" || !extrtypesarray.includes(type)) throw new Error("Invalid property fields.");

            let property: gextrprop = { name, description, type, validation_source, identificator, display_name, linked_fields, items };

            if (item.type == "string" || item.type == "number")
                property = { name, description, type, validation_source, identificator, display_name, linked_fields, items };
            if (type === "array" && Array.isArray(items)) property.items = parseJsonToProperties(items);
            if (type === "object") property.items = parseJsonToProperties(items);

            return property;
        });
    }
    const onRenderTitle = (options?: IDropdownOption[]) => {
        const selectedItem = options && options[0];
        return (
            <span
                className={selectedItem?.key === '' ? 'placeholder' : ''}
                style={{
                    color: selectedItem?.key === '' ? 'gray' : "#333",
                    fontStyle: selectedItem?.key === '' ? 'italic' : 'normal'
                }}
            >
                {selectedItem?.text || 'Validation'}
            </span>
        );
    };

    return (
        <div className={styles.container}>
            {isCalloutVisible && (
                <Callout
                    target={calloutTarget}
                    onDismiss={() => setIsCalloutVisible(false)}
                    directionalHint={DirectionalHint.bottomRightEdge}
                    gapSpace={10}
                    isBeakVisible={false}
                    styles={calloutStylesExportDocCallout}
                >
                    <div style={{ paddingTop: 5, paddingLeft: 20, paddingRight: 20, paddingBottom: 10 }}>
                        <TextField
                            className={styles.calloutImportScheme}
                            label="Paste JSON Scheme"
                            multiline
                            rows={5}
                            value={importJson}
                            onChange={(_, newValue) => setImportJson(newValue || "")}
                            styles={textFieldStylesDocExtractImportScheme}
                        />
                        <PrimaryButton text="Import" onClick={handleImport} style={{ marginTop: 10 }} />
                    </div>
                </Callout>
            )}
            {isRoot && (
                <div className={styles.importScheme}>

                    <h3>Data Definition</h3>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div className={styles.importSchemeImportButton} onClick={importScheme}>
                            <TooltipHost content="Import Extraction Scheme from JSON" directionalHint={DirectionalHint.bottomRightEdge}>
                                <TbArrowAutofitUp size={20} />
                            </TooltipHost>
                        </div>
                        <div className={styles.importSchemeImportButton} onClick={handleExport}>
                            <TooltipHost content="Export schema to clipboard" directionalHint={DirectionalHint.bottomRightEdge}>
                                <TbArrowAutofitDown size={20} />
                            </TooltipHost>
                        </div>
                    </div>
                </div>
            )}

            {prprts.length == 0 && (
                <div className={styles.noExtractDefinitionsContainer}>
                    <div className={styles.noExtractDefinitionsText}>Click 'Add New Field' to start defining the fields you want to get extracted </div>
                </div>
            )}
            {prprts &&  Array.isArray(prprts) &&
                prprts.map((prop, index) => (
                    <div key={index}
                        className={styles.fieldContainer}>
                        <Stack tokens={{ childrenGap: 5 }}>
                            <div className={styles.stackContainer}>
                                <div className={styles.fieldRow}>
                                    <div className={styles.fieldInputGroups}>
                                        <TextField
                                            placeholder="Field Identifier"
                                            value={prop.name}
                                            onChange={e => handleUpdate(index, { name: (e.target as HTMLInputElement).value?.replace(" ", "_") })}
                                            className={`${styles.fieldInput} ${styles.fieldColumnName}`}
                                            styles={textFieldStylesDocExtr}
                                        />
                                        <TextField
                                            placeholder="Display Name"
                                            value={prop.display_name}
                                            onChange={e => handleUpdate(index, { display_name: (e.target as HTMLInputElement).value })}
                                            className={`${styles.fieldInput} ${styles.fieldColumnName}`}
                                            styles={textFieldStylesDocExtr}
                                        />
                                    </div>
                                    <div className={styles.fieldInputGroups}>
                                        <Dropdown
                                            selectedKey={prop.type}
                                            options={extrTypeOptions}
                                            onChange={(e, option) =>
                                                handleUpdate(index, { type: option?.key as extrtypes })
                                            }
                                            className={`${styles.fieldInput} ${styles.fieldColumn}`}
                                            styles={dropdownStylesDocExtr}
                                        />

                                        {prop.type === "string" || prop.type === "number" ? (
                                            <Dropdown
                                                selectedKey={prop.validation_source || ''}
                                                placeholder="Validation Source"
                                                onRenderTitle={onRenderTitle}
                                                options={[
                                                    { key: '', text: '' }, // Empty option
                                                    ...(fileList?.map(file => ({ key: file.fileid.toString(), text: file.name })) || [])
                                                ]}
                                                onChange={(e, option) => handleUpdate(index, { validation_source: option?.key.toString() })}
                                                className={`${styles.fieldInput} ${styles.fieldColumn}`}
                                                styles={dropdownFileSrcStylesDocExtr}
                                            />
                                        ) : null}
                                    </div>
                                </div>

                                <TextField
                                    value={prop.description}
                                    onChange={e => handleUpdate(index, { description: (e.target as HTMLInputElement).value })}
                                    multiline
                                    placeholder="Description of the field purpose"
                                    className={`${styles.fieldInputMulti} ${styles.fieldInputFullWidth}`}
                                    styles={textFieldStylesMultilineShortDocExtr}
                                    autoAdjustHeight={true}
                                    resizable={false}
                                />
                            </div>

                            <div className={styles.removeButtonContainer}>

                                <Checkbox
                                    styles={ccaCheckBoxStyles}
                                    label="MCP"
                                    className={styles.checkBoxCca}
                                    title={"Look to collect this field always."}
                                    checked={prop.mcp_sourced === 1}
                                    onChange={(e, checked) => handleUpdate(index, { mcp_sourced: checked ? 1 : 0 })}
                                />
                                <Checkbox
                                    styles={ccaCheckBoxStyles}
                                    label="Global"
                                    className={styles.checkBoxCca}
                                    title={"Look to collect this field always."}
                                    checked={prop.global_prop}
                                    onChange={(e, checked) => handleUpdate(index, { global_prop: checked })}
                                />
                                <Checkbox
                                    styles={ccaCheckBoxStyles}
                                    label="ID"
                                    className={styles.checkBoxCcaID}
                                    title={"Is the field an exact sequence of numbers/letters (ex. Unified Civil Number, Phone Number, Card Number, Car Plate, etc.)?"}
                                    checked={prop.identificator}
                                    onChange={(e, checked) => handleUpdate(index, { identificator: checked })}
                                />
                                <div onClick={() => handleRemove(index)} className={styles.removeButton}>
                                    <FaTrashCan size={10} />
                                </div>
                            </div>
                            {prop.type === "array" && (
                                <div className={styles.arrayContainer}>
                                    <h3>List Items</h3>
                                    <CCAFieldSelection
                                        initialProperties={prop.items || []}
                                        onChange={subProps => {handleUpdate(index, { items: subProps })}}
                                        fileList={fileList}
                                        assistantType={assistantType}
                                        groupId={isRoot ? groupId: undefined}
                                    />
                                </div>
                            )}
                            {prop.type === "object" && (
                                <div className={styles.arrayContainer}>
                                    <h3>Object</h3>
                                    <CCAFieldSelection
                                        initialProperties={prop.items || []}
                                        onChange={subProps => {handleUpdate(index, { items: subProps })}}
                                        fileList={fileList}
                                        assistantType={assistantType}
                                        groupId={isRoot ? groupId: undefined}
                                    />
                                </div>
                            )}
                        </Stack>
                    </div>
                ))}
            <PrimaryButton text="Add New Field" onClick={handleAdd} className={styles.addButton} styles={addScenPrimaryButton}/>
        </div>
    );
};
